import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import PDFViewer from './PDFViewer';
import FieldsTable from './FieldsTable';
import { Plus, Upload, Download } from 'lucide-react';
import './FormFieldMapper.css';

function FormFieldMapper() {
  const [pdfPath, setPdfPath] = useState(null);
  const [, setJsonData] = useState(null);
  const [fields, setFields] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [highlightBox, setHighlightBox] = useState(null);
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [activeTab, setActiveTab] = useState('extracted');
  const [uploadedPdfs, setUploadedPdfs] = useState([]);
  const [extractionError, setExtractionError] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const fileInputRef = useRef(null);
  const jsonInputRef = useRef(null);

  // Load extracted JSON data from MongoDB (v2 API)
  useEffect(() => {
    const loadLatestResult = async () => {
      try {
        // Fetch uploaded PDFs from v2 API
        const pdfsResponse = await axios.get('/api/pdfs');
        if (pdfsResponse.data.length > 0) {
          setUploadedPdfs(pdfsResponse.data);

          // Load latest extraction output
          const outputsResponse = await axios.get('/api/outputs');
          if (outputsResponse.data.length > 0) {
            const latestOutput = outputsResponse.data[0];
            setJsonData(latestOutput.output);
            flattenJson(latestOutput.output);
          }
        }
      } catch (err) {
        console.log('No uploaded PDFs or extractions found yet');
      }
    };
    loadLatestResult();
  }, []);

  const flattenJson = (obj, prefix = '') => {
    const flatFields = [];
    const traverse = (o, p) => {
      Object.keys(o).forEach(key => {
        if (o[key] !== null && typeof o[key] === 'object' && !Array.isArray(o[key])) {
          traverse(o[key], p ? `${p}.${key}` : key);
        } else if (o[key]) {
          flatFields.push({
            id: Math.random(),
            path: p ? `${p}.${key}` : key,
            key: key,
            value: String(o[key]),
            verified: false
          });
        }
      });
    };
    traverse(obj, prefix);
    setFields(flatFields);
  };

  const handleFieldClick = (field) => {
    setSelectedField(field);
    // Simulate finding the value in PDF - in real implementation,
    // this would search for the text and return coordinates
    setHighlightBox({
      text: field.value,
      x: Math.random() * 800 + 100,
      y: Math.random() * 1000 + 100,
      width: field.value.length * 8,
      height: 30
    });
  };

  const handleAddCustomField = () => {
    if (newFieldName.trim()) {
      setCustomFields([...customFields, {
        id: Math.random(),
        name: newFieldName,
        value: '',
        color: `hsl(${Math.random() * 360}, 70%, 60%)`
      }]);
      setNewFieldName('');
      setShowAddField(false);
    }
  };

  const handleDragStart = (e, field) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('fieldValue', JSON.stringify(field));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDropOnCustomField = (e, customFieldId) => {
    e.preventDefault();
    const fieldData = JSON.parse(e.dataTransfer.getData('fieldValue'));

    setCustomFields(customFields.map(cf =>
      cf.id === customFieldId
        ? { ...cf, value: fieldData.value, sourceField: fieldData.path }
        : cf
    ));
  };

  const handleJsonUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target.result);
        setJsonData(json);
        flattenJson(json);
        alert('✅ JSON loaded! Fields are now available for mapping.');
      } catch (err) {
        alert('Invalid JSON file');
      }
    };
    reader.readAsText(file);
  };

  const handleExtractPdf = async () => {
    if (uploadedPdfs.length === 0) {
      alert('Please upload a PDF first');
      return;
    }

    const latestPdf = uploadedPdfs[0];

    try {
      setExtracting(true);
      setExtractionError(null);

      const response = await axios.post(`/api/extract-pdf/${latestPdf._id}`);

      if (response.data.output) {
        setJsonData(response.data.output);
        flattenJson(response.data.output);
        alert(`✅ Extraction successful!\nExtracted ${response.data.fields_count} fields from PDF`);
      }
    } catch (err) {
      if (err.response?.status === 501) {
        setExtractionError(
          '⚠️ Pipeline not available.\n\n' +
          'To extract data from PDF:\n' +
          '1. Run: python3 pipeline-main.py\n' +
          '2. Then click "Extract PDF" again\n\n' +
          'Or upload JSON file manually.'
        );
        alert('Pipeline not available. Please upload JSON file or run the pipeline.');
      } else {
        setExtractionError(`Extraction failed: ${err.message}`);
        alert(`❌ Extraction failed: ${err.message}`);
      }
    } finally {
      setExtracting(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setExtractionError(null);
      setExtracting(true);  // Show loader immediately
      setJsonData(null);    // Clear previous data
      setFields([]);        // Clear previous fields
      setCustomFields([]); // Clear previous mappings

      // 1. Upload to backend API
      const formData = new FormData();
      formData.append('file', file);
      formData.append('templateName', 'Others');

      const uploadResponse = await axios.post('/api/pdfs/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const pdfId = uploadResponse.data._id;
      const extractionStatus = uploadResponse.data.extraction_status;
      console.log('PDF uploaded:', uploadResponse.data);

      // 2. Display PDF in viewer (local blob)
      const reader = new FileReader();
      reader.onload = (event) => {
        const pdf = new Blob([event.target.result], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdf);
        setPdfPath(url);
      };
      reader.readAsArrayBuffer(file);

      // 3. Add to uploaded PDFs list
      setUploadedPdfs([
        {
          _id: pdfId,
          filename: uploadResponse.data.filename,
          uploadedAt: new Date().toISOString(),
          fileSize: file.size,
          status: extractionStatus === 'completed' ? 'extracted' : 'uploaded'
        },
        ...uploadedPdfs
      ]);

      // 4. Handle extraction result
      if (extractionStatus === 'completed') {
        // Pipeline auto-extracted data - fetch and display it
        try {
          const outputResponse = await axios.get(`/api/outputs/pdf/${pdfId}`);
          if (outputResponse.data && outputResponse.data.output) {
            setJsonData(outputResponse.data.output);
            flattenJson(outputResponse.data.output);
            setExtracting(false);
            alert(`✅ PDF uploaded & extracted!\nFound ${Object.keys(outputResponse.data.output).length} fields`);
          }
        } catch (err) {
          console.error('Failed to fetch extraction:', err);
          setExtracting(false);
        }
      } else if (extractionStatus === 'failed') {
        setExtracting(false);
        setExtractionError('Auto-extraction failed. You can still upload JSON or try manual extraction.');
        alert(`⚠️ Auto-extraction failed.\n\nOptions:\n1. Upload JSON file\n2. Click "Extract PDF" button\n3. Check GEMINI_API_KEY in .env`);
      } else {
        // No extraction attempted (pipeline not available)
        setExtracting(false);
        setExtractionError('💡 Upload a JSON file with extracted fields, or click "Extract PDF" to run extraction.');
        alert(`✅ PDF uploaded!\n\nOptions:\n1. Upload JSON file with extracted data\n2. Click "Extract PDF" button to run OCR extraction`);
      }

      // Switch to extracted fields tab to show results
      setActiveTab('extracted');
    } catch (err) {
      console.error('Upload error:', err);
      setExtracting(false);
      setExtractionError(`Upload failed: ${err.message}`);
      alert(`❌ Upload failed: ${err.message}`);
    }
  };

  const handleSaveMapping = async () => {
    try {
      const mapping = {
        originalFields: fields,
        customMappings: customFields,
        timestamp: new Date().toISOString()
      };

      // Try to save to MongoDB if we have extraction ID
      const formsResponse = await axios.get('/api/forms');
      if (formsResponse.data.length > 0) {
        const latestForm = formsResponse.data[0];
        const formId = latestForm._id;
        const extractionsResponse = await axios.get(`/api/extractions/form/${formId}`);

        if (extractionsResponse.data.length > 0) {
          const extraction = extractionsResponse.data[0];
          const extractionId = extraction._id;

          // Save to MongoDB
          const mappingResponse = await axios.post('/api/mappings', {
            extractionId,
            formId,
            customFields: customFields
          });

          const mappingId = mappingResponse.data._id;
          await axios.put(`/api/mappings/${mappingId}/export`);

          alert('✅ Mapping saved to database and exported!');
        }
      }

      // Also download as JSON file
      const element = document.createElement('a');
      element.setAttribute('href', 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(mapping, null, 2)));
      element.setAttribute('download', 'field-mapping.json');
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } catch (err) {
      console.error('Error saving mapping:', err);
      // Fallback to just downloading
      const mapping = {
        originalFields: fields,
        customMappings: customFields,
        timestamp: new Date().toISOString()
      };

      const element = document.createElement('a');
      element.setAttribute('href', 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(mapping, null, 2)));
      element.setAttribute('download', 'field-mapping.json');
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    }
  };

  return (
    <div className="form-mapper">
      {/* Header */}
      <header className="mapper-header">
        <div className="header-content">
          <h1>📋 KFintech OCR</h1>
          <p>Verify and map extracted form fields</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => jsonInputRef.current?.click()}>
            <Upload size={18} /> Upload JSON
          </button>
          <button className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>
            <Upload size={18} /> Upload PDF
          </button>
          {uploadedPdfs.length > 0 && (
            <button
              className="btn btn-warning"
              onClick={handleExtractPdf}
              disabled={extracting}
            >
              {extracting ? '🔄 Extracting...' : '⚡ Extract PDF'}
            </button>
          )}
          <button className="btn btn-primary" onClick={handleSaveMapping}>
            <Download size={18} /> Save Mapping
          </button>

          <input
            ref={jsonInputRef}
            type="file"
            accept=".json"
            onChange={handleJsonUpload}
            style={{ display: 'none' }}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handlePdfUpload}
            style={{ display: 'none' }}
          />
        </div>
      </header>

      {/* Main Content */}
      <div className="mapper-container">
        {/* Left Panel - PDF Viewer */}
        <div className="pdf-panel">
          <PDFViewer highlightBox={highlightBox} pdfPath={pdfPath} />
        </div>

        {/* Right Panel - Fields */}
        <div className="fields-panel">
          <div className="fields-tabs">
            <button
              className={`tab ${activeTab === 'extracted' ? 'active' : ''}`}
              onClick={() => setActiveTab('extracted')}
            >
              Extracted Fields
            </button>
            <button
              className={`tab ${activeTab === 'uploads' ? 'active' : ''}`}
              onClick={() => setActiveTab('uploads')}
            >
              Uploaded Files ({uploadedPdfs.length})
            </button>
            <button
              className={`tab ${activeTab === 'mappings' ? 'active' : ''}`}
              onClick={() => setActiveTab('mappings')}
            >
              Custom Mappings
            </button>
          </div>

          {/* Uploaded Files Tab */}
          {activeTab === 'uploads' && (
            <div className="fields-section">
              <div className="uploads-list">
                {uploadedPdfs.length === 0 ? (
                  <div className="empty-state">
                    <p>No PDFs uploaded yet</p>
                    <p className="hint">Click "Upload PDF" to add files</p>
                  </div>
                ) : (
                  uploadedPdfs.map(pdf => (
                    <div key={pdf._id} className="upload-item">
                      <div className="upload-header">
                        <span className="filename">📄 {pdf.filename}</span>
                        <span className="status">{pdf.status}</span>
                      </div>
                      <div className="upload-details">
                        <span>Size: {(pdf.fileSize / 1024).toFixed(2)} KB</span>
                        <span>Uploaded: {new Date(pdf.uploadedAt).toLocaleDateString()}</span>
                      </div>
                      <div className="upload-actions">
                        <button className="btn-small" onClick={() => setPdfPath(pdf._id)}>
                          View
                        </button>
                        <button className="btn-small">Download</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Extracted Fields Table */}
          {activeTab === 'extracted' && (
            <div className="fields-section">
              {extracting && (
                <div className="extraction-loader">
                  <div className="loader-spinner"></div>
                  <p>🚀 Processing PDF...</p>
                  <small>
                    📄 Step 1: Splitting pages<br/>
                    🧹 Step 2: Preprocessing images<br/>
                    ⚡ Step 3: Extracting data with AI<br/>
                    <br/>
                    This may take 30-60 seconds...
                  </small>
                </div>
              )}

              {extractionError && (
                <div className="extraction-error">
                  <p>{extractionError}</p>
                </div>
              )}

              {fields.length === 0 && !extracting ? (
                <div className="empty-fields">
                  <p>No fields extracted yet.</p>
                  <p className="hint">1. Upload PDF → 2. Click "Extract PDF" or upload JSON file → 3. Map fields</p>
                </div>
              ) : (
                <FieldsTable
                  fields={fields}
                  selectedField={selectedField}
                  onFieldClick={handleFieldClick}
                  onDragStart={handleDragStart}
                />
              )}
            </div>
          )}

          {/* Custom Fields Section */}
          {activeTab === 'mappings' && (
          <div className="custom-fields-section">
            <div className="custom-header">
              <h3>Custom Field Mappings</h3>
              <button
                className="btn-small"
                onClick={() => setShowAddField(!showAddField)}
              >
                <Plus size={16} /> Add Item
              </button>
            </div>

            {showAddField && (
              <div className="add-field-form">
                <input
                  type="text"
                  placeholder="Enter property name (e.g., applicant_retirement)"
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCustomField()}
                />
                <button onClick={handleAddCustomField}>Create</button>
                <button onClick={() => setShowAddField(false)}>Cancel</button>
              </div>
            )}

            <div className="custom-fields-list">
              {customFields.map(cf => (
                <div key={cf.id} className="custom-field-item">
                  <div className="field-label" style={{ borderLeftColor: cf.color }}>
                    <strong>{cf.name}</strong>
                    <span className="field-hint">Drag value here</span>
                  </div>
                  <div
                    className="drop-zone"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDropOnCustomField(e, cf.id)}
                  >
                    {cf.value ? (
                      <div className="dropped-value">
                        <span className="value">{cf.value}</span>
                        <span className="source">from: {cf.sourceField}</span>
                      </div>
                    ) : (
                      <span className="drop-hint">Drop field value here</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FormFieldMapper;
